import { getAuth } from 'firebase/auth';
import { Deck } from '../models/Deck';
import axios from 'axios';
import { FIREBASE_FUNCTION_URL } from '../app-settings';
import { SearchParams, SearchResponse } from 'typesense/lib/Typesense/Documents';
import { CreateFolderRequestBody, DeckFunctionRequest, DeleteFolderRequestBody, MoveDeckToFolderRequest, RenameFolderRequestBody, ReorderFoldersRequestBody } from '../models/CloudFunction';
import { Identifiable } from '../models/Common';

export async function createDeck(record: Deck): Promise<Identifiable> {
    const auth = getAuth();
    if (!auth.currentUser) {
        return Promise.reject();
    }

    const request: DeckFunctionRequest<Deck> = {
        body: record,
        functionName: "createdeck"
    }

    const userToken = await auth.currentUser.getIdToken();
    const response = await axios.post(`${FIREBASE_FUNCTION_URL}/decks`, request, { headers: { Authorization: `Bearer ${userToken}` } });
    return response.data;
}

export async function deleteDeck(id: string): Promise<void> {
    const auth = getAuth();
    if (!auth.currentUser) {
        return Promise.reject();
    }

    const requestBody: DeckFunctionRequest<{ id: string }> = {
        functionName: "deletedeck",
        body: { id: id }
    };

    const userToken = await auth.currentUser.getIdToken();
    await axios.post(`${FIREBASE_FUNCTION_URL}/decks`, requestBody, { headers: { Authorization: `Bearer ${userToken}` } });
}

export async function updateDeck(record: Deck): Promise<void> {
    const auth = getAuth();
    if (!auth.currentUser) {
        return Promise.reject();
    }

    const request: DeckFunctionRequest<Deck> = {
        body: record,
        functionName: "updatedeck"
    }

    const userToken = await auth.currentUser.getIdToken();
    await axios.post(`${FIREBASE_FUNCTION_URL}/decks`, request, { headers: { Authorization: `Bearer ${userToken}` } });
}

export async function getDeck(id: string): Promise<Deck> {
    const request: DeckFunctionRequest<Identifiable> = {
        body: { id: id },
        functionName: "getdeck"
    }

    const result = await axios.post(`${FIREBASE_FUNCTION_URL}/decks`, request);
    return result.data;
}

export async function likeDeck(id: string): Promise<void> {
    const auth = getAuth();
    if (!auth.currentUser) {
        return Promise.reject();
    }

    const request: DeckFunctionRequest<Identifiable> = {
        body: { id: id },
        functionName: "likedeck"
    }

    const userToken = await auth.currentUser.getIdToken();
    return await axios.post(`${FIREBASE_FUNCTION_URL}/decks`, request, { headers: { Authorization: `Bearer ${userToken}` } });
}

export async function unlikeDeck(id: string): Promise<void> {
    const auth = getAuth();
    if (!auth.currentUser) {
        return Promise.reject();
    }

    const request: DeckFunctionRequest<Identifiable> = {
        body: { id: id },
        functionName: "unlikedeck"
    }

    const userToken = await auth.currentUser.getIdToken();
    return await axios.post(`${FIREBASE_FUNCTION_URL}/decks`, request, { headers: { Authorization: `Bearer ${userToken}` } });
}

export async function searchLikedDecks(params: SearchParams): Promise<SearchResponse<Deck>> {
    try {
        const auth = getAuth();
        if (!auth.currentUser) {
            return Promise.reject();
        }

        const searchParams: SearchParams = {
            ...params,
            query_by: "serials, elements, name"
        };

        const requestBody: DeckFunctionRequest<SearchParams> = {
            functionName: "searchlikeddecks",
            body: searchParams
        };

        const userToken = await auth.currentUser.getIdToken();
        const results = await axios.post<SearchResponse<Deck>>(`${FIREBASE_FUNCTION_URL}/decks`, requestBody, { headers: { Authorization: `Bearer ${userToken}` } });
        return results.data;
    } catch (e) {
        return Promise.reject("Error fetching decks.");
    }
}

export async function viewDeck(id: string): Promise<void> {
    if (!id) {
        return Promise.reject();
    }

    const requestBody: DeckFunctionRequest<{ id: string }> = {
        functionName: "viewdeck",
        body: { id: id }
    };

    await axios.post(`${FIREBASE_FUNCTION_URL}/decks`, requestBody);
}

export async function searchAllDecks(params: SearchParams): Promise<SearchResponse<Deck>> {
    try {
        const searchParams: SearchParams = {
            ...params,
            query_by: "serials, elements, name"
        };

        const requestBody: DeckFunctionRequest<SearchParams> = {
            functionName: "searchalldecks",
            body: searchParams
        };

        const results = await axios.post<SearchResponse<Deck>>(`${FIREBASE_FUNCTION_URL}/decks`, requestBody);
        return results.data;
    } catch (e) {
        return Promise.reject("Error fetching decks.");
    }
}

export async function searchMyDecks(params: SearchParams): Promise<SearchResponse<Deck>> {
    try {
        const auth = getAuth();
        if (!auth.currentUser) {
            return Promise.reject();
        }

        const searchParams: SearchParams = {
            ...params,
            query_by: "serials, elements, name"
        };

        const requestBody: DeckFunctionRequest<SearchParams> = {
            functionName: "searchmydecks",
            body: searchParams
        };

        const userToken = await auth.currentUser.getIdToken();
        const results = await axios.post<SearchResponse<Deck>>(`${FIREBASE_FUNCTION_URL}/decks`, requestBody, { headers: { Authorization: `Bearer ${userToken}` } });

        return results.data;
    } catch (e) {
        return Promise.reject("Error fetching decks.");
    }
}

export async function getCustomTags(): Promise<string[]> {
    try {
        const auth = getAuth();
        if (!auth.currentUser) {
            return Promise.reject();
        }
    
        const requestBody: DeckFunctionRequest<undefined> = {
            functionName: "getcustomtags",
            body: undefined
        };

        const userToken = await auth.currentUser.getIdToken();
        const results = await axios.post<SearchResponse<Deck>>(`${FIREBASE_FUNCTION_URL}/decks`, requestBody, { headers: { Authorization: `Bearer ${userToken}` } });
        
        if (!results.data.facet_counts?.length) {
            return [];
        }

        const customTagsCount = results.data.facet_counts.find(x => x.field_name === "customTags");
        if (!customTagsCount) {
            return [];
        }

        return customTagsCount.counts.map(x => x.value).sort((a, b) => {
            return a < b ? -1 : a > b ? 1 : 0;
        });
    } catch (e) {
        return Promise.reject("Error fetching custom tags.");
    }
}

export async function moveDeckToFolder(id: string, folder: string): Promise<void> {
    if (!folder) {
        return;
    }

    const auth = getAuth();
    if (!auth.currentUser) {
        return Promise.reject();
    }

    const requestBody: MoveDeckToFolderRequest = {
        id: id,
        folder: folder
    }

    const request: DeckFunctionRequest<MoveDeckToFolderRequest> = {
        body: requestBody,
        functionName: "movedecktofolder"
    }

    const userToken = await auth.currentUser.getIdToken();
    return await axios.post(`${FIREBASE_FUNCTION_URL}/decks`, request, { headers: { Authorization: `Bearer ${userToken}` } });
}


export async function renameDeckFolder(oldFolderName: string, newFolderName: string): Promise<void> {
    if (!oldFolderName || !newFolderName) {
        return Promise.reject("Folder name cannot be empty");
    }

    const auth = getAuth();
    if (!auth.currentUser) {
        return Promise.reject("You must be logged in to rename deck folders.");
    }

    const requestBody: RenameFolderRequestBody = {
        new: newFolderName,
        old: oldFolderName
    }

    const request: DeckFunctionRequest<RenameFolderRequestBody> = {
        body: requestBody,
        functionName: "renamefolder"
    }

    const userToken = await auth.currentUser.getIdToken();
    return await axios.post(`${FIREBASE_FUNCTION_URL}/decks`, request, { headers: { Authorization: `Bearer ${userToken}` } });
}

export async function reorganizeFolders(folders: string[]): Promise<void> {
    if (!folders?.length) {
        return;
    }

    const auth = getAuth();
    if (!auth.currentUser) {
        return Promise.reject();
    }

    const requestBody: ReorderFoldersRequestBody = {
        folders: folders
    }

    const request: DeckFunctionRequest<ReorderFoldersRequestBody> = {
        body: requestBody,
        functionName: "reorderfolders"
    }

    const userToken = await auth.currentUser.getIdToken();
    return await axios.post(`${FIREBASE_FUNCTION_URL}/decks`, request, { headers: { Authorization: `Bearer ${userToken}` } });
}


export async function createDeckFolder(folder: string): Promise<void> {
    if (!folder?.length) {
        return Promise.reject("Folder name must be provided.");
    }

    if (folder.length > 25) {
        return Promise.reject("Folder name must be less than 26 characters.");
    }

    const auth = getAuth();
    if (!auth.currentUser) {
        return Promise.reject("Must be authenticated to create deck folders.");
    }

    const requestBody: CreateFolderRequestBody = {
        name: folder
    }

    const request: DeckFunctionRequest<CreateFolderRequestBody> = {
        body: requestBody,
        functionName: "createfolder"
    }

    const userToken = await auth.currentUser.getIdToken();
    return await axios.post(`${FIREBASE_FUNCTION_URL}/decks`, request, { headers: { Authorization: `Bearer ${userToken}` } });
}

export async function deleteDeckFolder(folder: string): Promise<void> {
    if (!folder?.length) {
        return Promise.reject("Folder name must be provided.");
    }

    const auth = getAuth();
    if (!auth.currentUser) {
        return Promise.reject("Must be authenticated to delete deck folders.");
    }

    const requestBody: DeleteFolderRequestBody = {
        name: folder
    }

    const request: DeckFunctionRequest<DeleteFolderRequestBody> = {
        body: requestBody,
        functionName: "deletefolder"
    }

    const userToken = await auth.currentUser.getIdToken();
    return await axios.post(`${FIREBASE_FUNCTION_URL}/decks`, request, { headers: { Authorization: `Bearer ${userToken}` } });
}